import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { List } from 'immutable';
import { compact, trim, upperCase } from 'lodash';
import {
  PaymentTransaction,
  PaymentTransactionStatusesEnum,
} from '../../../document-data/sub-models/payment-transaction.model';
import { IMasterListItemBuilder } from '../../../entity-master/components/master-list-item/master-list-item-builder.interface';
import {
  IMasterListItemAction,
  IMasterListItemData,
} from '../../../entity-master/components/master-list-item/master-list-item.interface';
import { TransactionActionsEnum } from '../../../payments-data/payments-data.data';
import { creditCardBrands } from '../../../payments/components/credit-cards/credit-cards.constant';
import { FriendlyDatePipe } from '../../../shared/pipes/friendly-date/friendly-date.pipe';
import { MoneyPipe } from '../../../shared/pipes/money/money.pipe';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { ITransactionAction } from '../transaction-history/transaction-history.component';

@Injectable()
export class TransactionHistoryListItemBuilder
  implements IMasterListItemBuilder {
  constructor(
    private _friendlyDatePipe: FriendlyDatePipe,
    private _moneyPipe: MoneyPipe,
    private _translateService: TranslateService,
    private _domSanitizer: DomSanitizer,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  buildTransactionNotes(paymentTransaction: PaymentTransaction) {
    // when the description exist and methodDetail is null, return null
    // because the content of description will be displayed in the same position as in methodDetail in this case
    if (!paymentTransaction.description || !paymentTransaction.methodDetail) {
      return null;
    }
    return paymentTransaction.description;
  }

  buildListItemData(
    paymentTransaction: PaymentTransaction,
    documentLocale: string,
    isDeposit: boolean
  ): List<IMasterListItemData> {
    let paymentDescription = '';
    let isHtml = false;
    let paymentNotes = '';

    if (paymentTransaction.isCreditCardTransaction()) {
      const type = paymentTransaction.creditCardType;
      const creditCardName = creditCardBrands[type] || upperCase(type);
      const creditCardInfo = `${creditCardName} **** **** **** ${paymentTransaction.cardLastFour}`;

      paymentDescription = isDeposit
        ? this._translateService.instant(
            'TRANSACTION_HISTORY.DEPOSIT_PAYMENT',
            { creditCardInfo }
          )
        : creditCardInfo;
    } else if (paymentTransaction.isPayPalTransaction()) {
      paymentDescription = this._translateService.instant(
        isDeposit
          ? 'TRANSACTION_HISTORY.PAYPAL_DEPOSIT_PAYMENT_DESCRIPTION'
          : 'TRANSACTION_HISTORY.PAYPAL_PAYMENT_DESCRIPTION',
        {
          payerEmail: paymentTransaction.payerEmail,
        }
      );
    } else if (paymentTransaction.isManualPayment()) {
      paymentDescription = paymentTransaction.methodDetail;
      paymentNotes = paymentTransaction.description;
    } else if (paymentTransaction.isBankTransferTransaction()) {
      paymentDescription = this._translateService.instant(
        'PAYMENTS.BANK_TRANSFER.LABEL'
      );

      if (isDeposit) {
        // TODO [clean_up]; create a porper string key for this translation
        paymentDescription = this._translateService.instant(
          'TRANSACTION_HISTORY.DEPOSIT_PAYMENT',
          {
            creditCardInfo: paymentDescription,
          }
        );
      }
    }

    if (paymentTransaction.isRefund()) {
      paymentDescription = this._translateService.instant(
        'TRANSACTION_HISTORY.REFUND_PAYMENT_DESCRIPTION',
        {
          paymentDescription: this._domSanitizer.sanitize(
            SecurityContext.HTML,
            paymentDescription
          ),
        }
      );
      isHtml = true;
    } else if (paymentTransaction.isDispute()) {
      paymentDescription = this._translateService.instant(
        'TRANSACTION_HISTORY.DISPUTE_PAYMENT_DESCRIPTION',
        {
          paymentDescription,
        }
      );
    }

    let paymentStatus = '';
    let statusClass = '';

    if (
      paymentTransaction.isBankTransferTransaction() ||
      paymentTransaction.isCreditCardTransaction() ||
      paymentTransaction.isPayPalTransaction()
    ) {
      const statusMapper = {
        [PaymentTransactionStatusesEnum.failed]: 'FAILED',
        [PaymentTransactionStatusesEnum.pending]: 'PENDING',
        [PaymentTransactionStatusesEnum.succeeded]: 'SUCCESSFUL',
      };
      const translationKey = statusMapper[paymentTransaction.status];

      if (translationKey) {
        // TODO [clean_up]: remove STATUS keys from BANK_TRANSFER
        paymentStatus = this._translateService.instant(
          `TRANSACTION_HISTORY.BANK_TRANSFER.STATUS.${translationKey}`
        );
      }

      if (PaymentTransactionStatusesEnum.failed === paymentTransaction.status) {
        statusClass = 'failed';
      }
    }

    return List([
      {
        values: [
          {
            value: this._friendlyDatePipe.transform(
              paymentTransaction.datetime
            ),
          },
        ],
        customClass: 'transaction-date',
      },
      {
        values: [
          {
            value: trim(paymentDescription || paymentNotes),
            isHtml,
          },
        ],
        customClass: paymentDescription
          ? 'transaction-description'
          : 'transaction-description-notes',
      },
      {
        values: [{ value: paymentStatus }],
        customClass: `transaction-status ${statusClass}`,
      },
      {
        values: [
          {
            value: this._moneyPipe.transform(
              paymentTransaction.amount,
              paymentTransaction.currencyCode,
              documentLocale
            ) as string,
          },
        ],
        customClass: 'transaction-amount',
      },
    ]);
  }

  buildListItemActions(
    paymentTransaction: PaymentTransaction,
    refundAction: IMasterListItemAction,
    unmatchAction: IMasterListItemAction,
    deleteAction: IMasterListItemAction,
    trackingObject: ITrackingObject,
    trackingScreenName: TrackingScreenNamesEnum
  ): List<List<IMasterListItemAction>> {
    let groups: List<List<IMasterListItemAction>> = List();

    if (
      !paymentTransaction.isRefundable() &&
      !paymentTransaction.isManualPayment()
    ) {
      const noRefundAction = {
        id: TransactionActionsEnum.no_refund_transaction,
        label: this._translateService.instant(
          'TRANSACTION_HISTORY.CANNOT_BE_REFUNDED'
        ),
        customClass: 'no-action',
      };

      groups = groups.push(List([noRefundAction]));
    }

    const seeInDashboardAction = paymentTransaction.isCreditCardTransaction()
      ? {
          id: TransactionActionsEnum.see_transaction_in_dashboard,
          label: this._translateService.instant('ACTION.VIEW_MORE_DETAILS'),
          trackingObject: trackingObject,
          trackingAction: this._trackingActionBuilder.buildButtonTapped(
            'see_in_dashboard',
            trackingScreenName
          ),
        }
      : null;
    const sendReceiptAction =
      paymentTransaction.isPayment() && !paymentTransaction.isFailed()
        ? {
            id: TransactionActionsEnum.send_receipt,
            label: this._translateService.instant('ACTION.SEND_RECEIPT'),
            trackingObject: trackingObject,
            trackingAction: this._trackingActionBuilder.buildButtonTapped(
              'send_receipt',
              trackingScreenName
            ),
          }
        : null;

    return groups.push(
      List(
        compact([
          seeInDashboardAction,
          sendReceiptAction,
          refundAction,
          unmatchAction,
          deleteAction,
        ])
      )
    );
  }

  buildDeleteAction(
    trackingObject: ITrackingObject,
    trackingScreenName: TrackingScreenNamesEnum
  ): IMasterListItemAction {
    return {
      id: TransactionActionsEnum.delete_transaction,
      label: this._translateService.instant('ACTION.DELETE'),
      deleteAction: true,
      trackingObject: trackingObject,
      trackingAction: this._trackingActionBuilder.buildButtonTapped(
        'delete_transaction',
        trackingScreenName
      ),
      e2eId: 'delete-action',
    };
  }

  buildRefundAction(
    trackingObject: ITrackingObject,
    trackingScreenName: TrackingScreenNamesEnum
  ): IMasterListItemAction {
    return {
      id: TransactionActionsEnum.refund_transaction,
      label: this._translateService.instant('ACTION.REFUND'),
      customClass: 'refund-action',
      trackingObject: trackingObject,
      trackingAction: this._trackingActionBuilder.buildButtonTapped(
        'refund_transaction',
        trackingScreenName
      ),
    };
  }

  buildUnmatchAction(
    trackingObject: ITrackingObject,
    trackingScreenName: TrackingScreenNamesEnum
  ): IMasterListItemAction {
    return {
      id: TransactionActionsEnum.unmatch_transaction,
      label: this._translateService.instant('ACTION.UNMATCH'),
      customClass: 'unmatch-action',
      trackingObject: trackingObject,
      trackingAction: this._trackingActionBuilder.buildButtonTapped(
        'undo_match',
        trackingScreenName
      ),
    };
  }

  buildTransactionAction(
    action: IMasterListItemAction,
    paymentTransaction: PaymentTransaction
  ): ITransactionAction {
    return {
      action,
      paymentTransaction,
    };
  }
}
